import type { DeviceType } from '../../../../../../global';
import { formatNumber } from '../../../util/number';

export const TileType = {
  P1: 'p1',
  P2: 'p2',
  P3: 'p3',
  P4: 'p4',
  P5: 'p5',
  L1: 'l1',
} as const;

export type TileType = (typeof TileType)[keyof typeof TileType];

export const LabelValue = {
  EXCLUSIVE: 'EXCLUSIVE',
  NEW: 'NEW',
  TOP10: 'TOP10',
} as const;

export type LabelValue = (typeof LabelValue)[keyof typeof LabelValue];

export const FetchPriority = {
  AUTO: 'auto',
  HIGH: 'high',
  LOW: 'low',
} as const;

export type FetchPriority = (typeof FetchPriority)[keyof typeof FetchPriority];

export const ImageLoading = {
  EAGER: 'eager',
  LAZY: 'lazy',
} as const;

export type ImageLoading = (typeof ImageLoading)[keyof typeof ImageLoading];

export const ImageDecoding = {
  ASYNC: 'async',
  SYNC: 'sync',
} as const;

export type ImageDecoding = (typeof ImageDecoding)[keyof typeof ImageDecoding];

export const Orientation = {
  PORTRAIT: 'PORTRAIT',
  LANDSCAPE: 'LANDSCAPE',
} as const;

export type Orientation = (typeof Orientation)[keyof typeof Orientation];

export type StudioAPI = {
  filterId: string;
  gamesTotalCount?: number;
  icon?: string;
  id: string;
  img: string /* ID of directus asset */;
  name: string;
  slug: string;
};

export type Studio = {
  filterId: string;
  gamesTotalCount: number;
  icon?: string;
  id: string;
  img: string /* ID of directus asset */;
  name: string;
  slug: string;
};

export type LobbyCategoryDisplayAPI = {
  limit: number;
  rank: boolean /* show numbers next to tiles */;
  rows: number;
  showLabels: boolean /* toggles the label (new, …) in the game tile */;
  showVideo?: boolean /* defaults to `true` from BE */;
  tile: TileType;
};

export type LobbyCategoryDisplay = {
  limit: number;
  rank: boolean;
  rows: number;
  shouldShowLabels: boolean;
  showVideo?: boolean;
  tile: TileType;
};

export class GameContent {
  description?: string | null;
  faqIds?: string[];
  features: string[] | null;
  gameType?: string;
  hasFunMode?: boolean;
  hitFrequency?: number;
  id: string;
  img: string;
  maxBet?: string;
  maxPayout?: string;
  maxWin?: number;
  minBet?: string;
  noIndex?: boolean;
  numBetMax?: number;
  numBetMin?: number;
  numReels?: number;
  numWinlines?: number;
  rtp?: number;
  slug: string;
  studio?: string;
  tags?: GameTag[];
  themes: string[] | null;
  title: string;
  vid: string;
  volatility?: number;

  public static fromGameDetails(gameDetails: GameDetails): GameContent {
    return { ...gameDetails } as GameContent;
  }
}

export type GameTag = {
  name: string;
  slug: string;
};

export type GameBase = {
  id: string /* example: ogs:70476 => path: /game/70476 */;
  img: string | null /* ID of directus asset */;
  title: string;
  vid?: string /* ID of directus asset */;
};

export type LobbyGame = GameBase & {
  exclude?: {
    devices?: DeviceType[];
    orientations?: Orientation[];
  };
  features: string[] | undefined;
  gameStudioId: string | undefined;
  gameType?: string;
  imgMobile: string | undefined /* ID of directus asset */;
  labels: LabelValue[] | undefined;
  latestWinAmount: string; // exists, if this field is present latestWinMultiplier & latestWinTime
  latestWinMultiplier: string;
  latestWinTime: string;
  maintenance: boolean | undefined;
  slug: string;
  themes: string[] | undefined;
};

export type GamesCategoryAPIResponse = {
  games: LobbyGame[];
};

export type SearchAPIResponse = {
  games: LobbyGame[];
};

export type GamesFilter = {
  filterId: string;
  isUserBased: boolean;
};

/**
 * A single game in the Best Match candidate pool, as returned by
 * `/lobbies/{lobbyId}/games/bestmatch`. Carries only the fields needed for
 * client-side volatility / theme / mechanic filtering.
 */
export type MatchPoolEntry = {
  id: string;
  // Absent when the game has no volatility recorded (1-5 when present).
  volatility?: number;
  // Slugs of every tag the game carries, across all categories.
  tagSlugs: string[];
};

export type PageDisplayAPI = {
  showLabels: boolean;
  tile: TileType;
};

export type PageDisplay = {
  shouldShowLabels: boolean;
  tile: TileType;
};

export type LobbyCategoryAPI = GamesFilter & {
  display: LobbyCategoryDisplayAPI;
  displayMobile: LobbyCategoryDisplayAPI | undefined;
  filterId: string;
  games: LobbyGame[] | undefined; // If `games` is undefined this category is a lazy category and the games must be fetched via /category?categoryId=super endpoint. App.instance.content.filterGames()
  gamesTotalCount?: number;
  page: PageDisplayAPI;
  pageMobile: PageDisplayAPI;
  slug: string;
  title: string;
  userBased: boolean;
};

export type LobbyCategory = GamesFilter & {
  display: LobbyCategoryDisplay;
  displayMobile: LobbyCategoryDisplay | undefined;
  filterId: string;
  games: LobbyGame[] | undefined; // If `games` is undefined this category is a lazy category and the games must be fetched via /category?categoryId=super endpoint. App.instance.content.filterGames()
  gamesTotalCount: number;
  page: PageDisplay;
  pageMobile: PageDisplay;
  slug: string;
  title: string;
  isUserBased: boolean;
};

export type LobbyAPI = {
  categories: LobbyCategoryAPI[];
  id: string;
  searchCategories: LobbyCategoryAPI[];
  searchTags: SearchTagAPI[];
  studios: StudioAPI[];
  tags: TagAPI[];
};

export type Lobby = {
  categories: LobbyCategory[];
  id: string;
  searchCategories: LobbyCategory[];
  searchTags: SearchTag[];
  studios: Studio[];
  tags: Tag[];
};

export type TagAPI = {
  name: string;
  slug: string;
};

export type Tag = {
  name: string;
  slug: string;
};

export type SearchTagAPI = {
  name: string;
  slug: string;
};

export type SearchTag = {
  name: string;
  slug: string;
};

export type MigratedGameId = {
  gameId: string;
};

export class GameDetails {
  description?: string;
  hasFunMode?: boolean;
  id: string;
  img: string;
  slug: string;
  themes?: string[];
  title: string;
  features: string[];

  vid?: string;

  private readonly gameType?: string;
  private readonly maxBet?: number;
  private readonly minBet?: number;
  private readonly rtp?: number;
  private readonly studio?: string;

  constructor(
    payload: any,
    private readonly moneyFormatter: (val: number) => string,
  ) {
    for (const key in payload) {
      // @ts-expect-error
      this[key] = payload[key];
    }
  }

  public getMaxBet(): string {
    // 10000 -> $100.0
    return this.maxBet ? this.moneyFormatter(this.maxBet) : '-';
  }

  public getMinBet(): string {
    // 20 -> $0.20
    return this.minBet ? this.moneyFormatter(this.minBet) : '-';
  }

  public getRtp(): number | undefined {
    return this.rtp;
  }

  // 96.22 -> 96,22%
  public getRtpString(): string {
    return this.rtp !== undefined ? `${formatNumber(this.rtp)}%` : '-';
  }

  public getStudio(): string {
    return this.studio ?? '-';
  }

  public getGameType(): string {
    return this.gameType ?? '-';
  }
}

export type Rule = {
  left: string;
  op: string;
  right: Array<string>;
};

export type RuleSet = {
  rules: Array<Rule>;
};

export type GameInfo = {
  bundleId?: string;
  created?: Date;
  description?: string;
  devices?: DeviceType[];
  displaySets?: unknown;
  faqIds?: string[];
  features?: string[];
  gameId?: string;
  gameStudioId?: string;
  gameType?: string;
  genres?: string[];
  hasFunMode?: boolean;
  hitFrequency?: number;
  integration?: string;
  integrationGameId?: string;
  jurisdictions?: string[];
  languages?: string;
  launchId?: string;
  launchIdDesktop?: string;
  launchIdMobile?: string;
  maxBet?: number;
  maxPayout?: number;
  maxWin?: number;
  mechanics?: string[];
  minBet?: number;
  modified?: Date;
  noIndex?: boolean;
  numBetMax?: number;
  numBetMin?: number;
  numReels?: number;
  numWinlines?: number;
  orientations?: Orientation[];
  regId?: string;
  rtp?: number;
  slug?: string;
  studio?: string;
  themes?: string[];
  title?: string;
  volatility?: number;
};

export const gameInfoSubsetFields = ['gameId', 'title', 'rtp', 'studio', 'bundleId'];
export type GameInfoSubset = Readonly<Required<Pick<GameInfo, 'gameId' | 'title' | 'rtp' | 'studio' | 'bundleId'>>>;
